import { Box, FlagField, FormModal, SpinnerButton, TextField } from '@forgeglobal/design-framework'
import { Field, Form, FormRenderProps } from 'react-final-form'
import { useIssuerPlatformSettingsQuery, useUpdateIssuerPlatformSettingsMutation } from 'graphql/bindings/AdminBindings'

import { MoneyFormattedInput } from 'components/shared/NumericInput'
import { formatSharePrice, parseNumber } from 'util/NumberUtils'

import { MutationResult } from 'react-apollo'
import React from 'react'

interface FormShape {
  companyId: number
  showPricingData: boolean
  defaultSharePriceInvestor?: number
  defaultSharePriceShareholder?: number
  featured: boolean
  mutationResult: MutationResult
}

const PlatformSettingsForm: React.FC<FormRenderProps<FormShape>> = ({ handleSubmit, form, values, valid }) => {
  const mutateInProgress = values.mutationResult.called
  return (
    <form onSubmit={handleSubmit}>
      <Field
        validate={(value: any, allValues: any) => {
          if (allValues.showPricingData) {
            return value === null || parseNumber(value) <= 0 || parseNumber(value) > 1000000
          }
          return false
        }}
        name="defaultSharePriceShareholder"
        parse={value => parseNumber(value)}
        InputProps={{
          inputComponent: MoneyFormattedInput as any,
        }}
        render={props => {
          return (
            <TextField
              placeholder={
                values.defaultSharePriceShareholder ? formatSharePrice(values.defaultSharePriceShareholder) : '$0.00'
              }
              label="Implied Bid (price per share)"
              helperText={props.meta.error ?? ' '}
              {...props}
            />
          )
        }}
      />
      <Field
        validate={(value: any, allValues: any) => {
          if (allValues.showPricingData) {
            return value === null || parseNumber(value) <= 0 || parseNumber(value) > 1000000
          }
          return false
        }}
        name="defaultSharePriceInvestor"
        parse={value => parseNumber(value)}
        InputProps={{
          inputComponent: MoneyFormattedInput as any,
        }}
        render={props => {
          return (
            <TextField
              placeholder={
                values.defaultSharePriceInvestor ? formatSharePrice(values.defaultSharePriceInvestor) : '$0.00'
              }
              label="Implied Ask (price per share)"
              helperText={props.meta.error ?? ' '}
              {...props}
            />
          )
        }}
      />
      <Field
        name="showPricingData"
        render={props => {
          return <FlagField label="Show Pricing Data" {...props} />
        }}
      />
      <Field
        name="featured"
        render={props => {
          return <FlagField label="Featured" {...props} />
        }}
      />
      <Box mt={2}>
        <SpinnerButton
          disabled={!valid}
          variant="contained"
          color="primary"
          type="submit"
          fullWidth
          loading={mutateInProgress}
          caption="Save"
        />
      </Box>
    </form>
  )
}

export const PlatformSettingsModal: React.FC<{ hideModal: () => void; issuerId: string }> = ({
  hideModal,
  issuerId,
}) => {
  const { data } = useIssuerPlatformSettingsQuery({ variables: { issuerId: issuerId } })
  const company = data?.account.issuer_account?.company!
  const [mutate, mutateResult] = useUpdateIssuerPlatformSettingsMutation({
    onCompleted: hideModal,
  })
  const onSubmit = (values: FormShape) => {
    mutate({
      variables: {
        companyPlatformSettings: {
          companyId: values.companyId,
          show_pricing_data: values.showPricingData,
          default_share_price_investor: values.defaultSharePriceInvestor,
          default_share_price_shareholder: values.defaultSharePriceShareholder,
          featured: values.featured,
        },
      },
    })
  }

  const initialValues: FormShape = {
    companyId: company.id,
    showPricingData: company?.show_pricing_data ?? false,
    defaultSharePriceInvestor: company?.default_share_price_investor,
    defaultSharePriceShareholder: company?.default_share_price_shareholder,
    featured: company?.featured ?? false,
    mutationResult: mutateResult,
  }
  return (
    <FormModal open hideModal={hideModal} title="Edit: Platform Settings">
      <Form<FormShape>
        component={PlatformSettingsForm}
        onSubmit={onSubmit}
        initialValues={{
          ...initialValues,
        }}
        validate={(values: FormShape) => {
          const errors: { [key: string]: string } = {}

          if (
            (values.defaultSharePriceInvestor && !values.defaultSharePriceShareholder) ||
            (values.defaultSharePriceShareholder && !values.defaultSharePriceInvestor)
          ) {
            errors['defaultSharePriceShareholder'] = 'Both prices are required'
          } else if (
            values.defaultSharePriceInvestor &&
            values.defaultSharePriceShareholder &&
            values.defaultSharePriceInvestor < values.defaultSharePriceShareholder
          ) {
            errors['defaultSharePriceShareholder'] = 'Implied ask must be greater than implied bid'
          }

          return errors
        }}
      />
    </FormModal>
  )
}
